import type { components } from "../../api/generated-schema";

export type PublicCemeteryMapData = components["schemas"]["PublicCemeteryMapData"];
export type PublicCemeteryMapPoint = components["schemas"]["PublicCemeteryMapPoint"];
export class PublicCemeteryMapError extends Error {
  constructor(readonly requestId?: string) { super("Không tải được bản đồ nghĩa trang"); }
}

export async function fetchPublicCemeteryMap(signal: AbortSignal): Promise<PublicCemeteryMapData> {
  const response = await fetch("/api/public/cemeteries/map", {
    credentials: "omit", signal,
    headers: { Accept: "application/json, application/problem+json", "X-Request-Id": crypto.randomUUID() },
  });
  let body: unknown;
  try { body = await response.json(); }
  catch (error) {
    if (signal.aborted) throw error;
    throw new PublicCemeteryMapError(response.headers.get("X-Request-Id") ?? undefined);
  }
  const envelope = body as { success?: unknown; data?: unknown; requestId?: unknown } | null;
  const requestId = typeof envelope?.requestId === "string" ? envelope.requestId : undefined;
  const data = envelope?.data as { points?: unknown; updatedAt?: unknown } | null;
  if (!response.ok || envelope?.success !== true || !data || !Array.isArray(data.points)
    || typeof data.updatedAt !== "string" || !/T.*(?:Z|[+-]\d{2}:\d{2})$/.test(data.updatedAt)
    || Number.isNaN(Date.parse(data.updatedAt))) throw new PublicCemeteryMapError(requestId);
  for (const candidate of data.points) {
    const point = candidate as Partial<PublicCemeteryMapPoint> | null;
    if (!point || typeof point.name !== "string" || !point.name.trim()
      || typeof point.address !== "string"
      || !((point.latitude === null && point.longitude === null)
        || (typeof point.latitude === "number" && Number.isFinite(point.latitude) && Math.abs(point.latitude) <= 90
          && typeof point.longitude === "number" && Number.isFinite(point.longitude) && Math.abs(point.longitude) <= 180))
      || typeof point.graveCount !== "number" || !Number.isSafeInteger(point.graveCount) || point.graveCount < 0
      || (point.scenePath !== null && (typeof point.scenePath !== "string"
        || !/^\/public\/cemeteries\/[a-z0-9-]+\/3d$/.test(point.scenePath)))
      || (point.googlePlaceId != null && (typeof point.googlePlaceId !== "string"
        || !/^[A-Za-z0-9_-]{10,255}$/.test(point.googlePlaceId)))) throw new PublicCemeteryMapError(requestId);
  }
  return data as PublicCemeteryMapData;
}
